import {
  PayloadAction,
  createAsyncThunk,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';
import {
  VisitorBlock,
  BlockedVisitor,
  RrwebEventMetaMode,
  UniqueVisitor,
  IntegrationService,
  VisitorSession,
} from 'sb-lib/types';
import { analytics } from 'shared/utils/analytics';
import {
  Client,
  ConsolidatedSession,
  UnisearchResults,
  UnwrapPromise,
  ActiveVisitor,
  UserAvailability,
  ClientEdit,
  CrmContact,
  VisitorContextualRecording,
  Page,
  OrgAnalyticsData,
} from 'types';
import {
  createVisitorBlock,
  deleteVisitorBlock,
  getUniqueVisitor,
  getUniqueVisitorBy,
  getVisitorSessions,
  queryUnisearch,
  getUserAvailability,
  patchUserAvailability,
  postUserAvailability,
  deleteUserAvailability,
  updateUniqueVisitor,
  createOrgCrmContact,
  getVisitorContextualRecordings,
  getOrgAnalytics,
} from 'shared/api';
import { AppState } from './reducers';
import { RejectError } from 'shared/api/axios';
import {
  fulfilledMoreablePage,
  makeInitialMoreableState,
  pendingMoreablePage,
} from 'shared/utils/helpers';
import { mediaDevices, MediaDevices } from 'shared/utils/media-devices';
import {
  orgPlanUsageUpdated,
  receivedOrgAvailability,
  callWanted,
  refreshActiveVisitor,
} from './shared-actions';
import { selectActiveOrg } from './auth-slice';
import {
  associationWanted,
  peerConnectionErrorUpdate,
  recordingWanted,
  takeoverWanted,
} from './realtime-slice';
import { consolidateSessions } from './data-util';
import {
  setMediaDevices,
  setOutgoingAudioEnabled,
  setOutgoingVideoEnabled,
} from 'shared/realtime';
import { selectActiveUser, selectActiveVisitor } from './shared-selectors';
import { clearAllRecordedRrwebEvents } from 'shared/realtime/rrweb-event-sink';
import { addManyCases } from 'shared/utils/redux';
import { getActiveTimePreference } from 'shared/utils/time';
import { rrwebEventHandlerClearAll } from 'shared/realtime/rrweb-event-handler';
import { formatISO, sub } from 'date-fns';
import log from 'shared/utils/log';

export { refreshActiveVisitor } from './shared-actions';

export type Filter = {
  type: 'path' | 'name' | 'customId' | 'email';
  value: string;
};

export interface ContextualRecordingsState {
  sbid: string;
  loadTime: number;
  isLoading: boolean;
  pages: Array<Page<VisitorContextualRecording>>;
  error: string;
  indexToPlay: number;
}

type TemplateSidebar = 'list' | 'details' | 'hidden';

// STATE
export const getInitialState = () => ({
  unisearchQuery: '',
  unisearchPaths: [] as string[],
  unisearchPathsTotalCount: 0,
  unisearchNames: [] as string[],
  unisearchNamesTotalCount: 0,
  unisearchCustomIds: [] as string[],
  unisearchCustomIdsTotalCount: 0,
  unisearchEmails: [] as string[],
  unisearchEmailsTotalCount: 0,
  isUnisearching: false,
  hasUnisearched: false,
  unisearchError: '',

  activeFilter: null as null | Filter,
  activeFilterUniqueVisitors: [] as Client[],
  isActivatingFilter: false,
  activateFilterError: '',

  activeVisitorId: null as null | string,
  activateVisitorError: '',
  activeVisitor: null as null | ActiveVisitor,

  activeVisitorSessions: makeInitialMoreableState<VisitorSession>(),
  activeVisitorConsolidatedSessions: [] as ConsolidatedSession[],
  activeVisitorFirstSession: null as VisitorSession | null,

  isUsageLoaded: false,
  orgProactiveCallsRemaining: null as number | null,
  orgReactiveCallsRemaining: null as number | null,
  orgTakeoversRemaining: null as number | null,
  orgSpyViewingsRemaining: null as number | null,
  orgSixsenseCompanyDetailFetchesRemaining: null as number | null,
  isShowingTakeoverUsageLimit: false,
  isShowingVideoUsageLimit: false,
  isShowingSpyViewingsUsageLimit: false,
  isShowingChatUsageLimit: false,
  isShowingGenericLimit: false,
  genericLimitNameSingular: null as string | null,
  genericLimitNamePlural: null as string | null,

  isAvailabilityLoaded: false,
  available: false,
  availableAdminIds: [] as number[],
  isUserAvailabilityLoaded: false,
  userAvailability: null as UserAvailability | null,

  isOutgoingVideoEnabled: true,
  isOutgoingAudioEnabled: true,
  wasPlayDeniedNoInteraction: false,
  audioDevices: [] as MediaDevices,
  audioOutputDevices: [] as MediaDevices,
  videoDevices: [] as MediaDevices,
  preferredAudioDeviceId: undefined as undefined | string,
  preferredAudioOutputDeviceId: undefined as undefined | string,
  preferredVideoDeviceId: undefined as undefined | string,
  preferredMediaDeviceError: '',

  isReportingIssue: false,

  rrwebIngressQueueLastDrainLatency: 0,
  rrwebIngressLastDuplicateEventTime: 0,

  isAwaitingInitialSnapshot: true,
  isAwaitingReplayerFirstRender: true,
  replayerMode: null as null | RrwebEventMetaMode,

  isNewVersionAvailable: false,

  isDrawActive: false,

  isGreetingWanted: null as null | boolean,

  contextualRecordingsMap: {} as Record<string, ContextualRecordingsState>,

  prevTemplateSidebar: 'list' as TemplateSidebar,
  templateSidebar: 'list' as TemplateSidebar,

  analyticsDay: null as OrgAnalyticsData | null,
  analyticsPrevDay: null as OrgAnalyticsData | null,
  analyticsWeek: null as OrgAnalyticsData | null,
  analyticsPrevWeek: null as OrgAnalyticsData | null,
  analyticsMonth: null as OrgAnalyticsData | null,
  analyticsPrevMonth: null as OrgAnalyticsData | null,
});

export type DashboardState = ReturnType<typeof getInitialState>;

// THUNKS
export const doUnisearch = createAsyncThunk<
  UnisearchResults,
  DashboardState['unisearchQuery'],
  { rejectValue: RejectError }
>('dashboard/doUnisearch', async query => {
  const res = await queryUnisearch(query);
  return res.data;
});

export const activateFilter = createAsyncThunk<
  { clients?: Client[]; filter: Filter },
  Filter,
  { state: AppState }
>('dashboard/activateFilter', async filter => {
  if (filter.type === 'name') {
    const res = await getUniqueVisitorBy({ displayName: filter.value });
    return { clients: res.data, filter };
  }
  if (filter.type === 'customId') {
    const res = await getUniqueVisitorBy({ customId: filter.value });
    return { clients: res.data, filter };
  }
  if (filter.type === 'email') {
    const res = await getUniqueVisitorBy({ email: filter.value });
    return { clients: res.data, filter };
  }
  if (filter.type === 'path') {
    const res = await getUniqueVisitorBy({ path: filter.value });
    return { clients: res.data, filter };
  }

  return { filter };
});

export const activateVisitor = createAsyncThunk<
  UniqueVisitor,
  { visitorId: string; isSwitch?: boolean },
  { state: AppState }
>(
  'dashboard/activateVisitor',
  async (data, { getState, dispatch }) => {
    const state = getState();

    if (selectIsSpyViewsLimited(state)) {
      dispatch(setIsShowingSpyViewingsUsageLimit(true));
      analytics.track('Usage Limit Modal Shown', { spyViewing: true });
    }

    const { visitorId, isSwitch } = data;
    if (!isSwitch) {
      void dispatch(peerConnectionErrorUpdate(''));
      void dispatch(associationWanted({ visitorId }));
    }
    const res = await getUniqueVisitor(visitorId);
    return res.data;
  },
  {
    condition: ({ visitorId }, { getState }) => {
      // Ignore activation when already active
      const isAlreadyActiveOrActivating =
        getState().dashboard.activeVisitorId === visitorId;
      return !isAlreadyActiveOrActivating;
    },
  },
);

export const deactivateVisitor = createAsyncThunk<void, void, { state: AppState }>(
  'dashboard/deactivateVisitor',
  async (_, { dispatch }) => {
    void dispatch(associationWanted({ visitorId: null }));
    dispatch(dashboardSlice.actions.clearActiveVisitor());
    dispatch(clearActiveVisitorSessions());
    dispatch(recordingWanted(false));
    dispatch(setIsDrawActive(false));
    rrwebEventHandlerClearAll();
    clearAllRecordedRrwebEvents();
  },
  {
    condition: (_, { getState }) => {
      // Only deactivate if we have an activeVisitorId && we're not on an active call
      return (
        !!getState().dashboard.activeVisitorId &&
        !['active', 'establishing'].includes(getState().realtime.callStatus)
      );
    },
  },
);

export const editVisitor = createAsyncThunk<Client, ClientEdit>(
  'dashboard/editVisitor',
  async client => {
    const res = await updateUniqueVisitor(client);
    return res.data;
  },
);

export const createCrmContact = createAsyncThunk<
  void,
  { service: IntegrationService; data: CrmContact }
>('dashboard/createCrmContact', async ({ service, data }) => {
  const res = await createOrgCrmContact(service, data);
  return res.data;
});

export const getAnalytics = createAsyncThunk<OrgAnalyticsData[], void>(
  'dashboard/getAnalytics',
  async () => {
    const today = formatISO(Date.now(), { representation: 'date' });
    const dayAgo = formatISO(sub(Date.now(), { days: 1 }), { representation: 'date' });
    const prevDayAgo = formatISO(sub(Date.now(), { days: 2 }), {
      representation: 'date',
    });
    const weekAgo = formatISO(sub(Date.now(), { weeks: 1 }), { representation: 'date' });
    const prevWeekAgo = formatISO(sub(Date.now(), { weeks: 2 }), {
      representation: 'date',
    });
    const monthAgo = formatISO(sub(Date.now(), { months: 1 }), {
      representation: 'date',
    });
    const prevMonthAgo = formatISO(sub(Date.now(), { months: 2 }), {
      representation: 'date',
    });

    const res = await Promise.all([
      getOrgAnalytics(dayAgo, today),
      getOrgAnalytics(prevDayAgo, dayAgo),
      getOrgAnalytics(weekAgo, today),
      getOrgAnalytics(prevWeekAgo, weekAgo),
      getOrgAnalytics(monthAgo, today),
      getOrgAnalytics(prevMonthAgo, monthAgo),
    ]);

    return res.map(r => r.data);
  },
);

export const addVisitorBlock = createAsyncThunk<
  BlockedVisitor,
  VisitorBlock,
  { state: AppState }
>('dashboard/addVisitorBlock', async ({ id, note, status, blockType, action }) => {
  const res = await createVisitorBlock(id, note, status, blockType, action);
  return res.data;
});

export const removeVisitorBlock = createAsyncThunk<
  BlockedVisitor,
  string,
  { state: AppState; rejectValue: RejectError }
>('dashboard/removeVisitorBlock', async (id: string) => {
  const res = await deleteVisitorBlock(id);
  return res.data;
});

export const fetchActiveVisitorSessions = createAsyncThunk<
  UnwrapPromise<ReturnType<typeof getVisitorSessions>>['data'],
  void,
  { state: AppState }
>('dashboard/fetchActiveVisitorSessions', async (_, { getState }) => {
  const { activeVisitorId, activeVisitorSessions } = getState().dashboard;
  if (!activeVisitorId) {
    throw new Error('Cannot get visitor sessions if no active visitor');
  }
  const { page } = activeVisitorSessions;
  const res = await getVisitorSessions(activeVisitorId, { page });
  return res.data;
});

export const startCall = createAsyncThunk<
  void,
  {
    activeConversationId: number;
    video?: boolean;
  },
  { state: AppState }
>(
  'dashboard/startCall',
  async ({ activeConversationId, video = true }, { getState, dispatch }) => {
    const state = getState();
    const activeVisitor = selectActiveVisitor(state);
    if (activeVisitor?.isVideo) {
      throw new Error('Already on a call');
    }
    if (!activeConversationId) {
      throw new Error('Not on a conversation');
    }
    const reactive = activeVisitor?.hasDialed;
    const selectIsCallLimited = reactive
      ? selectIsReactiveCallLimited
      : selectIsProactiveCallLimited;
    if (selectIsCallLimited(state)) {
      dispatch(setIsShowingVideoUsageLimit(true));
      analytics.track('Usage Limit Modal Shown', { call: true, proactive: !reactive });
      return;
    }
    dispatch(
      callWanted({
        callConversationId: activeConversationId,
        intentionSource: 'agent',
        video,
      }),
    );
    dispatch(setOutgoingMediaEnabled({ isOutgoingVideoEnabled: video }));
  },
);

export const setPreferredMediaDevices = createAsyncThunk<
  { audioDeviceId?: string; audioOutputDeviceId?: string; videoDeviceId?: string },
  { audioDeviceId?: string; audioOutputDeviceId?: string; videoDeviceId?: string },
  { state: AppState }
>(
  'dashboard/setPreferredMediaDevices',
  async ({ audioDeviceId, videoDeviceId, audioOutputDeviceId }, { getState }) => {
    await setMediaDevices(audioDeviceId, videoDeviceId);
    // Re-disable any tracks that should be disabled.
    const { isOutgoingAudioEnabled, isOutgoingVideoEnabled } = getState().dashboard;
    setOutgoingAudioEnabled(isOutgoingAudioEnabled);
    setOutgoingVideoEnabled(isOutgoingVideoEnabled);
    return { audioDeviceId, videoDeviceId, audioOutputDeviceId };
  },
);

export const populateMediaDevices = createAsyncThunk(
  'dashboard/populateMediaDevices',
  async () => {
    const devices = await mediaDevices.getMediaDevices();
    const {
      audio: preferredAudioDeviceId,
      video: preferredVideoDeviceId,
      audioOutput: preferredAudioOutputDeviceId,
    } = await mediaDevices.getPreferredMediaDevices();
    return {
      ...devices,
      preferredAudioDeviceId,
      preferredVideoDeviceId,
      preferredAudioOutputDeviceId,
    };
  },
);

export const endCall = createAsyncThunk<void, void, { state: AppState }>(
  'dashboard/endCall',
  async (_, { dispatch }) => {
    await dispatch(callWanted({ callConversationId: 0, intentionSource: 'agent' }));
  },
);

export const startTakeover = createAsyncThunk<void, void, { state: AppState }>(
  'dashboard/startTakeover',
  async (_, { getState, dispatch }) => {
    const state = getState();
    const activeVisitor = selectActiveVisitor(state);
    if (activeVisitor?.isTakeover) {
      throw new Error('Already doing takeover');
    }
    if (selectIsTakeoverLimited(state)) {
      dispatch(setIsShowingTakeoverUsageLimit(true));
      analytics.track('Usage Limit Modal Shown', { takeover: true });
      return;
    }
    dispatch(takeoverWanted(true));
  },
);

export const endTakeover = createAsyncThunk<void, void, { state: AppState }>(
  'dashboard/endTakeover',
  async (_, { dispatch }) => {
    dispatch(takeoverWanted(false));
  },
);

export const setOutgoingMediaEnabled = createAsyncThunk<
  { isOutgoingAudioEnabled?: boolean; isOutgoingVideoEnabled?: boolean },
  { isOutgoingAudioEnabled?: boolean; isOutgoingVideoEnabled?: boolean },
  { state: AppState }
>(
  'dashboard/setOutgoingMediaEnabled',
  async ({ isOutgoingAudioEnabled, isOutgoingVideoEnabled }) => {
    if (typeof isOutgoingAudioEnabled !== 'undefined') {
      setOutgoingAudioEnabled(isOutgoingAudioEnabled);
    }

    if (typeof isOutgoingVideoEnabled !== 'undefined') {
      setOutgoingVideoEnabled(isOutgoingVideoEnabled);
    }
    return { isOutgoingAudioEnabled, isOutgoingVideoEnabled };
  },
);

export const fetchUserAvailability = createAsyncThunk<UserAvailability | null>(
  'dashboard/fetchUserAvailability',
  async () => {
    try {
      return (await getUserAvailability()).data;
    } catch (err) {
      if (err?.response?.status === 404) {
        return null;
      }
      throw err;
    }
  },
);

export const updateOrCreateUserAvailability = createAsyncThunk<
  UserAvailability,
  Parameters<typeof postUserAvailability>[0],
  { state: AppState }
>('dashboard/updateOrCreateAvailability', async (args, { getState }) => {
  const ua = selectUserAvailability(getState());
  if (ua && ua.isManual) {
    return (await patchUserAvailability(ua.id, args)).data;
  } else {
    return (await postUserAvailability(args)).data;
  }
});

export const clearUserAvailability = createAsyncThunk<void, string, { state: AppState }>(
  'dashboard/clearUserAvailability',
  async id => {
    try {
      await deleteUserAvailability(id);
    } catch (err) {
      // If not found, just assume that availability was cleared.
      if (err?.response?.status === 404) {
        throw err;
      }
    }
  },
);

export const fetchMoreContextualRecordings = createAsyncThunk<
  Page<VisitorContextualRecording>,
  { sbid: string; reset?: boolean },
  { state: AppState }
>(
  'dashboard/fetchMoreContextualRecordings',
  async ({ sbid }, { getState }) => {
    const { contextualRecordingsMap } = getState().dashboard;
    const cr = contextualRecordingsMap[sbid] || {
      sbid,
      loadTime: 0,
      isLoading: true,
      pages: [],
      error: '',
      indexToPlay: -1,
    };
    const pageNumber = cr.pages.length + 1;
    const res = await getVisitorContextualRecordings(sbid, pageNumber);
    if (res.data) {
      if (res.data.page !== pageNumber) {
        throw new Error(
          `Failed to load correct page, got ${res.data.page} instead of ${pageNumber}`,
        );
      }
      return res.data;
    } else {
      throw new Error(`Could not get contextual recordings, data missing in response`);
    }
  },
  {
    condition: ({ sbid }, { getState }) => {
      // skip if loading already
      const { contextualRecordingsMap } = getState().dashboard;
      const cr = contextualRecordingsMap[sbid];
      return !(cr && cr.isLoading);
    },
  },
);

const resetUnisearchResults = (state: DashboardState) => {
  state.unisearchPaths = [];
  state.unisearchNames = [];
  state.unisearchCustomIds = [];
  state.unisearchEmails = [];
  state.unisearchPathsTotalCount = 0;
  state.unisearchNamesTotalCount = 0;
  state.unisearchCustomIdsTotalCount = 0;
  state.unisearchEmailsTotalCount = 0;
};

// SLICE
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: getInitialState(),
  reducers: {
    resetDashboardState() {
      return getInitialState();
    },
    resetUnisearchState(state) {
      state.unisearchQuery = '';
      resetUnisearchResults(state);
      state.isUnisearching = false;
      state.hasUnisearched = false;
      state.unisearchError = '';
    },
    removeActiveFilter(state) {
      state.activeFilter = null;
      state.isActivatingFilter = false;
      state.activateFilterError = '';
      state.activeFilterUniqueVisitors = [];
    },
    clearActiveVisitor(state) {
      state.activeVisitor = null;
      state.activeVisitorId = null;
      state.activateVisitorError = '';
    },
    clearActiveVisitorSessions(state) {
      state.activeVisitorSessions = makeInitialMoreableState<VisitorSession>();
    },
    setWasPlayDeniedNoInteraction(state, action: PayloadAction<boolean>) {
      state.wasPlayDeniedNoInteraction = action.payload;
    },
    setIsShowingTakeoverUsageLimit(state, action: PayloadAction<boolean>) {
      state.isShowingTakeoverUsageLimit = action.payload;
    },
    setIsShowingChatUsageLimit(state, action: PayloadAction<boolean>) {
      state.isShowingChatUsageLimit = action.payload;
    },
    setIsShowingVideoUsageLimit(state, action: PayloadAction<boolean>) {
      state.isShowingVideoUsageLimit = action.payload;
    },
    setIsShowingSpyViewingsUsageLimit(state, action: PayloadAction<boolean>) {
      state.isShowingSpyViewingsUsageLimit = action.payload;
    },
    setIsShowingGenericLimit(
      state,
      action: PayloadAction<{ show: boolean; singular?: string; plural?: string }>,
    ) {
      state.isShowingGenericLimit = action.payload.show;
      state.genericLimitNameSingular = action.payload.singular || null;
      state.genericLimitNamePlural = action.payload.plural || null;
    },
    clearPreferredMediaDeviceError(state) {
      state.preferredMediaDeviceError = '';
    },
    setIsReportingIssue(state, action: PayloadAction<boolean>) {
      state.isReportingIssue = action.payload;
    },
    awaitingInitialSnapshot(state, action: PayloadAction<boolean>) {
      state.isAwaitingInitialSnapshot = action.payload;
    },
    awaitingReplayerFirstRender(state, action: PayloadAction<boolean>) {
      state.isAwaitingReplayerFirstRender = action.payload;
    },
    replayerModeChanged(state, action: PayloadAction<RrwebEventMetaMode>) {
      state.replayerMode = action.payload;
    },
    setIsNewVersionAvailable(state, action: PayloadAction<boolean>) {
      state.isNewVersionAvailable = action.payload;
    },
    setIsDrawActive(state, action: PayloadAction<boolean>) {
      state.isDrawActive = action.payload;
    },
    setIsGreetingWanted(state, action: PayloadAction<boolean | null>) {
      state.isGreetingWanted = action.payload;
    },
    updateRrwebIngressHealth(
      state,
      action: PayloadAction<{ queueLatency: number; lastDuplicateTime: number }>,
    ) {
      state.rrwebIngressQueueLastDrainLatency = action.payload.queueLatency;
      state.rrwebIngressLastDuplicateEventTime = action.payload.lastDuplicateTime;
    },
    updateContextualRecordingToPlay(state, action: PayloadAction<number>) {
      if (state.activeVisitorId && state.contextualRecordingsMap[state.activeVisitorId]) {
        state.contextualRecordingsMap[state.activeVisitorId].indexToPlay = action.payload;
      }
    },
    setTemplateSidebar(state, action: PayloadAction<TemplateSidebar>) {
      state.prevTemplateSidebar = state.templateSidebar;
      state.templateSidebar = action.payload;
    },
    setPreferredAudioDeviceId(state, action: PayloadAction<undefined | string>) {
      state.preferredAudioDeviceId = action.payload;
    },
  },
  extraReducers: builder => {
    // analytics
    builder.addCase(getAnalytics.fulfilled, (state, action) => {
      state.analyticsDay = action.payload[0];
      state.analyticsPrevDay = action.payload[1];
      state.analyticsWeek = action.payload[2];
      state.analyticsPrevWeek = action.payload[3];
      state.analyticsMonth = action.payload[4];
      state.analyticsPrevMonth = action.payload[5];
    });
    // doUnisearch
    builder.addCase(doUnisearch.pending, (state, action) => {
      state.unisearchQuery = action.meta.arg;
      resetUnisearchResults(state);
      state.isUnisearching = true;
      state.unisearchError = '';
    });
    builder.addCase(doUnisearch.fulfilled, (state, action) => {
      if (state.unisearchQuery === action.meta.arg) {
        state.unisearchNames = action.payload.names;
        state.unisearchPaths = action.payload.paths;
        state.unisearchCustomIds = action.payload.customIds;
        state.unisearchEmails = action.payload.emails;
        state.unisearchNamesTotalCount = action.payload.namesTotalCount;
        state.unisearchPathsTotalCount = action.payload.pathsTotalCount;
        state.unisearchCustomIdsTotalCount = action.payload.customIdsTotalCount;
        state.unisearchEmailsTotalCount = action.payload.emailsTotalCount;
        state.isUnisearching = false;
        state.hasUnisearched = true;
      }
    });
    builder.addCase(doUnisearch.rejected, (state, action) => {
      resetUnisearchResults(state);
      state.isUnisearching = false;
      state.hasUnisearched = true;
      state.unisearchError = action.error?.message || '';
    });

    // activateFilter
    builder.addCase(activateFilter.pending, (state, action) => {
      resetUnisearchResults(state);
      state.hasUnisearched = false;
      state.activeFilter = action.meta.arg;
      state.activeFilterUniqueVisitors = [];
      state.isActivatingFilter = true;
      state.activateFilterError = '';
    });
    builder.addCase(activateFilter.fulfilled, (state, action) => {
      const { clients } = action.payload;
      if (clients) {
        state.activeFilterUniqueVisitors = clients;
      }
      state.isActivatingFilter = false;
    });
    builder.addCase(activateFilter.rejected, (state, action) => {
      state.activeFilter = null;
      state.activateFilterError = action.error?.message || '';
    });

    // activateVisitor
    builder.addCase(activateVisitor.pending, (state, action) => {
      const sbid = action.meta.arg.visitorId;
      const visitorFromFilter =
        state.activeFilterUniqueVisitors.find(uv => uv.sbid === sbid) || {};
      const optimisticVisitor = {
        ...visitorFromFilter,
        isLoading: true,
        sbid,
        id: sbid,
      } as ActiveVisitor;
      state.activeVisitor = optimisticVisitor;

      state.activeVisitorId = sbid;
      state.activateVisitorError = '';
    });
    builder.addCase(activateVisitor.fulfilled, (state, action) => {
      const visitor = action.payload;
      if (state.activeVisitor) {
        state.activeVisitor = {
          ...(state.activeVisitor || {}),
          ...visitor,
          ...{ isLoading: false, hasLoaded: true },
        };
      } else {
        state.activateVisitorError = 'Expected active visitor to be set in dashboard';
      }

      state.activeVisitorId = visitor.id;
      log.debug(`activateVisitor.fulfilled state.activeVisitorId = ${visitor.id}`);
      if (state.activeVisitor) {
        state.activeVisitor.isLoading = false;
        state.activeVisitor.hasLoaded = true;
      }
    });
    builder.addCase(activateVisitor.rejected, (state, action) => {
      if (state.activeVisitor) {
        state.activeVisitor.isLoading = false;
      }
      // Ignore abort error (from thunk.abort() async cancellation)
      if (action.error.name !== 'AbortError') {
        state.activateVisitorError =
          action.error.message ||
          `Could not activate visitor becuase of ${action.error.name} error`;
      }
    });

    builder.addCase(refreshActiveVisitor.fulfilled, (state, action) => {
      const visitor = action.payload;
      if (state.activeVisitor && visitor) {
        state.activeVisitor = {
          ...(state.activeVisitor || {}),
          ...visitor,
          ...{ isLoading: false, hasLoaded: true },
        };
      }
    });

    // blockVisitor
    builder.addCase(addVisitorBlock.fulfilled, (state, action) => {
      const { action: blockAction, blockType, id, status } = action.payload;
      const block = { action: blockAction, blockType, id, status };
      if (state.activeVisitor) {
        const blocks = state.activeVisitor.blocks
          ? [block, ...state.activeVisitor.blocks]
          : [block];
        state.activeVisitor = {
          ...state.activeVisitor,
          blocks,
        };
      }
    });
    builder.addCase(removeVisitorBlock.fulfilled, (state, action) => {
      const blockId = action.meta.arg;
      if (state.activeVisitor) {
        const blocks = state.activeVisitor.blocks || [];
        const blockIndex = blocks.findIndex(block => block.id === blockId);
        delete blocks[blockIndex];
        state.activeVisitor = {
          ...state.activeVisitor,
          blocks,
        };
      }
    });

    // editVisitor
    builder.addCase(editVisitor.fulfilled, (state, action) => {
      const editedVisitor = action.payload;
      if (state.activeVisitor) {
        state.activeVisitor = {
          ...state.activeVisitor,
          ...editedVisitor,
        };
      }
    });

    // fetchActiveVisitorSessions
    builder.addCase(fetchActiveVisitorSessions.pending, state => {
      if (state.activeVisitorId) {
        state.activeVisitorSessions = pendingMoreablePage(
          state.activeVisitorSessions,
          {},
        );
        state.activeVisitorFirstSession = null;
      }
    });
    builder.addCase(fetchActiveVisitorSessions.fulfilled, (state, action) => {
      if (state.activeVisitorId) {
        state.activeVisitorSessions = fulfilledMoreablePage(
          state.activeVisitorSessions,
          action.payload,
        );
        if (action.payload.firstSession) {
          state.activeVisitorFirstSession = action.payload.firstSession;
        }
        consolidateSessions(state);
      }
    });

    // setOutgoingMediaEnabled
    builder.addCase(setOutgoingMediaEnabled.fulfilled, (state, action) => {
      const { isOutgoingAudioEnabled, isOutgoingVideoEnabled } = action.payload;
      if (typeof isOutgoingAudioEnabled !== 'undefined') {
        state.isOutgoingAudioEnabled = isOutgoingAudioEnabled;
      }
      if (typeof isOutgoingVideoEnabled !== 'undefined') {
        state.isOutgoingVideoEnabled = isOutgoingVideoEnabled;
      }
    });

    // setPreferredMediaDevices
    builder.addCase(setPreferredMediaDevices.pending, state => {
      state.preferredMediaDeviceError = '';
    });
    builder.addCase(setPreferredMediaDevices.fulfilled, (state, action) => {
      const { audioDeviceId, videoDeviceId, audioOutputDeviceId } = action.payload;
      state.preferredAudioDeviceId = audioDeviceId || state.preferredAudioDeviceId;
      state.preferredAudioOutputDeviceId =
        audioOutputDeviceId || state.preferredAudioOutputDeviceId;
      state.preferredVideoDeviceId = videoDeviceId || state.preferredVideoDeviceId;
    });
    builder.addCase(setPreferredMediaDevices.rejected, (state, action) => {
      state.preferredMediaDeviceError = action.error.message || 'Unknown reason';
    });

    // populateMediaDevices
    builder.addCase(populateMediaDevices.fulfilled, (state, action) => {
      const {
        preferredAudioDeviceId,
        preferredVideoDeviceId,
        preferredAudioOutputDeviceId,
      } = action.payload;
      state.audioDevices = action.payload.audioInputDevices.map(x => x);
      state.videoDevices = action.payload.videoInputDevices.map(x => x);
      state.audioOutputDevices = action.payload.audioOutputDevices.map(x => x);

      state.preferredAudioDeviceId =
        preferredAudioDeviceId || state.preferredAudioDeviceId;

      state.preferredAudioOutputDeviceId =
        preferredAudioOutputDeviceId || state.preferredAudioOutputDeviceId;

      state.preferredVideoDeviceId =
        preferredVideoDeviceId || state.preferredVideoDeviceId;
    });

    builder.addCase(receivedOrgAvailability, (state, action) => {
      const { available, availableAdminIds } = action.payload;
      state.isAvailabilityLoaded = true;
      state.available = available;
      state.availableAdminIds = availableAdminIds;
    });

    builder.addCase(orgPlanUsageUpdated, (state, action) => {
      const {
        orgTakeoversRemaining,
        orgProactiveCallsRemaining,
        orgReactiveCallsRemaining,
        orgSpyViewingsRemaining,
        orgSixsenseCompanyDetailFetchesRemaining,
      } = action.payload;
      state.isUsageLoaded = true;
      state.orgProactiveCallsRemaining = orgProactiveCallsRemaining;
      state.orgReactiveCallsRemaining = orgReactiveCallsRemaining;
      state.orgTakeoversRemaining = orgTakeoversRemaining;
      state.orgSpyViewingsRemaining = orgSpyViewingsRemaining;
      state.orgSixsenseCompanyDetailFetchesRemaining = orgSixsenseCompanyDetailFetchesRemaining;
    });

    // callWanted
    builder.addCase(callWanted.fulfilled, (state, action) => {
      if (!action.meta.arg.callConversationId) {
        // Reset mic / cam mute state when call ends
        state.isOutgoingAudioEnabled = true;
        state.isOutgoingVideoEnabled = true;
      }
    });

    // fetchUserAvailability and updateOrCreateUserAvailability
    addManyCases(
      builder,
      [fetchUserAvailability.fulfilled, updateOrCreateUserAvailability.fulfilled],
      (state, action) => {
        state.userAvailability = action.payload;
        state.isUserAvailabilityLoaded = true;
      },
    );

    // clearUserAvailability
    builder.addCase(clearUserAvailability.fulfilled, state => {
      state.userAvailability = null;
    });

    // fetchMoreContextualRecordings
    builder.addCase(fetchMoreContextualRecordings.pending, (state, action) => {
      if (!state.contextualRecordingsMap[action.meta.arg.sbid] || action.meta.arg.reset) {
        state.contextualRecordingsMap[action.meta.arg.sbid] = {
          sbid: action.meta.arg.sbid,
          loadTime: 0,
          isLoading: true,
          pages: [],
          error: '',
          indexToPlay: -1,
        };
      }

      state.contextualRecordingsMap[action.meta.arg.sbid].isLoading = true;
    });
    builder.addCase(fetchMoreContextualRecordings.fulfilled, (state, action) => {
      const { sbid } = action.meta.arg;
      state.contextualRecordingsMap[sbid].isLoading = false;
      state.contextualRecordingsMap[sbid].loadTime = Date.now();
      state.contextualRecordingsMap[sbid].pages = [
        ...state.contextualRecordingsMap[sbid].pages,
        action.payload,
      ];
    });
    builder.addCase(fetchMoreContextualRecordings.rejected, (state, action) => {
      const { sbid } = action.meta.arg;
      state.contextualRecordingsMap[sbid].isLoading = false;
      if (action.error.message) {
        state.contextualRecordingsMap[sbid].error =
          action.error.message || 'Failed to load recordings';
      }
    });
  },
});

export const selectEnforceUsageLimits = (state: AppState) => {
  if (!state.dashboard.isUsageLoaded) return false;
  return selectActiveOrg(state)?.plan.enforceUsageLimits || false;
};

export const selectIsProactiveCallLimited = (state: AppState) => {
  const enforceLimits = selectEnforceUsageLimits(state);
  const { orgProactiveCallsRemaining } = state.dashboard;
  return (
    enforceLimits &&
    orgProactiveCallsRemaining !== null &&
    orgProactiveCallsRemaining <= 0
  );
};

export const selectIsReactiveCallLimited = (state: AppState) => {
  const enforceLimits = selectEnforceUsageLimits(state);
  const { orgReactiveCallsRemaining } = state.dashboard;
  return (
    enforceLimits && orgReactiveCallsRemaining !== null && orgReactiveCallsRemaining <= 0
  );
};

export const selectIsTakeoverLimited = (state: AppState) => {
  const enforceLimits = selectEnforceUsageLimits(state);
  const { orgTakeoversRemaining } = state.dashboard;
  return enforceLimits && orgTakeoversRemaining !== null && orgTakeoversRemaining <= 0;
};

export const selectIsSpyViewsLimited = (state: AppState) => {
  const enforceLimits = selectEnforceUsageLimits(state);
  const { orgSpyViewingsRemaining } = state.dashboard;
  return (
    enforceLimits && orgSpyViewingsRemaining !== null && orgSpyViewingsRemaining <= 0
  );
};

export const selectActiveVisitorSessions = (state: AppState) => {
  if (state.tour.isTouring) {
    return {
      ...makeInitialMoreableState(),
      hasFetched: true,
    };
  }
  return state.dashboard.activeVisitorSessions;
};

export const selectUserAvailability = (state: AppState) => {
  return state.dashboard.userAvailability;
};

/**
 * Select the active org's configured time preference that is active right now.
 */
export const selectActiveOrgCurrentTimePreference = createSelector(
  (state: AppState) => selectActiveOrg(state),
  (state: AppState) => selectActiveUser(state),
  (activeOrg, activeUser) => {
    if (!activeOrg) return null;
    if (activeUser?.timePreferences && activeUser?.timePreferences.length > 0) {
      return getActiveTimePreference(activeUser.timePreferences, activeOrg.timezone);
    }
    return getActiveTimePreference(activeOrg.timePreferences, activeOrg.timezone);
  },
);

export const {
  resetDashboardState,
  resetUnisearchState,
  removeActiveFilter,
  clearActiveVisitor,
  clearActiveVisitorSessions,
  setWasPlayDeniedNoInteraction,
  setIsShowingTakeoverUsageLimit,
  setIsShowingVideoUsageLimit,
  setIsShowingSpyViewingsUsageLimit,
  setIsShowingChatUsageLimit,
  setIsShowingGenericLimit,
  clearPreferredMediaDeviceError,
  setIsReportingIssue,
  awaitingInitialSnapshot,
  awaitingReplayerFirstRender,
  replayerModeChanged,
  setIsNewVersionAvailable,
  setIsDrawActive,
  setIsGreetingWanted,
  updateRrwebIngressHealth,
  updateContextualRecordingToPlay,
  setTemplateSidebar,
  setPreferredAudioDeviceId,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
